/** Безопасные карточки авторов из доступной истории, без запроса закрытого списка участников. */
import { useMemo } from 'react'

import type { Message, PublicPhoto } from '@shared/api/types'

export interface MessageSender {
  name: string | null
  photo: PublicPhoto | null
}

export type SenderMap = ReadonlyMap<string, MessageSender>

/** Палитра имён: цвет закреплён за человеком, а не за местом в списке. */
const SENDER_COLORS = 6

/**
 * Устойчивый цвет имени по id: одинаковый у всех участников и между заходами.
 *
 * Хеш — FNV-1a со сдвигом в конце, а не привычное `hash * 31 + c`: 31 ≡ 1 (mod 6), поэтому
 * с ним остаток зависел только от суммы кодов, и соседние uuid v7 (общий длинный префикс,
 * одинаковый набор цифр) сплошь и рядом получали один цвет — в группе это и проверилось.
 */
export function senderColor(userId: string): string {
  let hash = 2166136261
  for (let i = 0; i < userId.length; i += 1) {
    hash ^= userId.charCodeAt(i)
    hash = Math.imul(hash, 16777619)
  }
  hash ^= hash >>> 15
  return `var(--color-sender-${((hash >>> 0) % SENDER_COLORS) + 1})`
}

/** Карточки есть и у reply-preview, даже если исходное сообщение ещё не загружено. */
export function useChannelMembers(messages: readonly Message[]): SenderMap {
  return useMemo(() => {
    const senders = new Map<string, MessageSender>()
    for (const message of messages) {
      for (const sender of [message.reply_to?.sender, message.sender]) {
        if (sender) senders.set(sender.id, { name: sender.display_name, photo: sender.photo })
      }
    }
    return senders
  }, [messages])
}
